import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { counterReadingsApi, type ListReadingsParams } from './service';
import type {
  RecordCounterReadingPayload,
  SupersedeCounterReadingPayload,
} from './types';

export const counterReadingsKeys = {
  all: ['counter-readings'] as const,
  list: (equipmentId: string, params: ListReadingsParams = {}) =>
    [...counterReadingsKeys.all, 'list', equipmentId, params] as const,
  latest: (equipmentId: string) =>
    [...counterReadingsKeys.all, 'latest', equipmentId] as const,
  due: (filters: { vesselId?: string; companyId?: string } = {}) =>
    [...counterReadingsKeys.all, 'due', filters] as const,
};

export function useEquipmentCounterReadings(
  equipmentId: string | undefined,
  params: ListReadingsParams = {},
) {
  return useQuery({
    queryKey: equipmentId
      ? counterReadingsKeys.list(equipmentId, params)
      : counterReadingsKeys.all,
    queryFn: () => counterReadingsApi.listForEquipment(equipmentId as string, params),
    enabled: Boolean(equipmentId),
  });
}

export function useLatestEquipmentReadings(equipmentId: string | undefined) {
  return useQuery({
    queryKey: equipmentId
      ? counterReadingsKeys.latest(equipmentId)
      : counterReadingsKeys.all,
    queryFn: () => counterReadingsApi.latestForEquipment(equipmentId as string),
    enabled: Boolean(equipmentId),
  });
}

export function useCounterReadingsDue(
  filters: { vesselId?: string; companyId?: string } = {},
) {
  return useQuery({
    queryKey: counterReadingsKeys.due(filters),
    queryFn: () => counterReadingsApi.due(filters),
  });
}

export function useRecordCounterReading(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordCounterReadingPayload) =>
      counterReadingsApi.record(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: counterReadingsKeys.all });
      void equipmentId; // explicit dependency for the cache scope
    },
  });
}

export function useSupersedeCounterReading() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ readingId, input }: { readingId: string; input: SupersedeCounterReadingPayload }) =>
      counterReadingsApi.supersede(readingId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: counterReadingsKeys.all });
    },
  });
}
